import {
  type CardQuality,
  CraftGameplayTier,
  GameplayTier,
} from '@sorare/core/src/__generated__/globalTypes';
import type { PlayerPoolTier } from '@sorare/core/src/lib/playerPoolTiers';
import {
  GAMEPLAY_TIER_POOL_TIERS,
  QUALITY_POOL_TIERS,
  parsePlayerPoolTierSlug,
  playerPoolTierSlug,
} from '@sorare/core/src/lib/playerPoolTiers';

type Chest = {
  rarity: string;
  thresholds: {
    draws: {
      probabilities: { quality: CardQuality }[];
      drawProbabilities: { quality: CraftGameplayTier }[];
    }[];
  }[];
};

const CRAFT_TIER_GAMEPLAY_TIERS: Record<CraftGameplayTier, GameplayTier[]> = {
  [CraftGameplayTier.GOAT]: [GameplayTier.GOAT],
  [CraftGameplayTier.STAR]: [GameplayTier.STAR],
  [CraftGameplayTier.IMPACT]: [GameplayTier.IMPACT],
  [CraftGameplayTier.ROSTER]: [GameplayTier.ROSTER],
  [CraftGameplayTier.DNP]: [GameplayTier.DNP],
  [CraftGameplayTier.STAR_OR_GOAT]: [GameplayTier.STAR, GameplayTier.GOAT],
};

const draws = (chest: Chest) =>
  chest.thresholds.flatMap(threshold => threshold.draws);

const chestPoolTiers = (
  chest: Chest,
  withGameplayTier: boolean
): PlayerPoolTier[] => {
  if (withGameplayTier) {
    return draws(chest)
      .flatMap(draw => draw.drawProbabilities)
      .flatMap(({ quality }) => CRAFT_TIER_GAMEPLAY_TIERS[quality])
      .map(gameplayTier => ({ gameplayTier }));
  }

  return draws(chest)
    .flatMap(draw => draw.probabilities)
    .map(({ quality }) => ({ quality }));
};

export const craftPoolTiersByRarity = (
  chests: Chest[],
  withGameplayTier: boolean,
  selectedTierSlug?: string
) => {
  const raritiesByTierSlug: Record<string, string[]> = {};

  chests.forEach(chest => {
    chestPoolTiers(chest, withGameplayTier).forEach(tier => {
      const slug = playerPoolTierSlug(tier);
      if (!raritiesByTierSlug[slug]?.includes(chest.rarity)) {
        raritiesByTierSlug[slug] = [
          ...(raritiesByTierSlug[slug] || []),
          chest.rarity,
        ];
      }
    });
  });

  const orderedTiers = withGameplayTier
    ? GAMEPLAY_TIER_POOL_TIERS
    : QUALITY_POOL_TIERS;

  const tiers = orderedTiers.filter(
    tier => raritiesByTierSlug[playerPoolTierSlug(tier)]?.length
  );

  const unlistedSelectedTier =
    selectedTierSlug && !raritiesByTierSlug[selectedTierSlug]?.length
      ? parsePlayerPoolTierSlug(selectedTierSlug)
      : undefined;

  if (unlistedSelectedTier && selectedTierSlug) {
    tiers.push(unlistedSelectedTier);
    raritiesByTierSlug[selectedTierSlug] = [
      ...new Set(chests.map(chest => chest.rarity)),
    ];
  }

  return { tiers, raritiesByTierSlug };
};
